import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, ChevronUp, Leaf, Wind, Award } from 'lucide-react';
import SectionWrapper from '../UI/SectionWrapper';

const Bio = () => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(imageRef.current, {
        scale: 0.8,
        opacity: 0,
        duration: 0.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 50%",
        }
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const highlights = [
    { icon: <Leaf className="w-5 h-5" />, text: "20+ Years Landscape Design" },
    { icon: <Wind className="w-5 h-5" />, text: "10+ Years Ren Xue Qigong" },
    { icon: <Award className="w-5 h-5" />, text: "Certified Practitioner" },
  ];

  return (
    <SectionWrapper ref={containerRef} id="bio" className="bg-earthy-sand/30 max-w-7xl mx-auto my-12 rounded-[3rem]">
      <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-16">
        
        {/* Organic Image Mask - Sticky on Desktop */}
        <div className="w-full md:flex-1 flex justify-center md:sticky md:top-24 md:self-start">
          <div 
            ref={imageRef}
            className="w-72 h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-cover bg-center shadow-2xl transition-all duration-250"
            style={{ 
              backgroundImage: 'url(/amelia-dp.png)', // Placeholder
              borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%' // Organic blob shape
            }}
          ></div>
        </div>

        <div className="flex-1 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-serif text-forest-green mb-4 leading-tight">
            Amelia Downie – Certified Land Healing Practitioner
          </h2>
          
          {/* Highlights Row */}
          <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
            {highlights.map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-white/60 px-3 py-1.5 rounded-full text-sm font-medium text-forest-green/80 shadow-sm border border-forest-green/10">
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>

          <div className="space-y-4 text-forest-green/90 font-sans leading-relaxed">
            <p className="text-lg font-medium">
              Amelia Downie is a Certified Land Healing Practitioner, trained by Maggie Landman, bridging the gap between physical landscape design and energetic wellbeing.
            </p>

            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="space-y-4 pt-2">
                <p>
                  With nearly two decades of experience in landscape design, Amelia understands that a space is more than just its physical form, plant ecology, and functionality—it is also defined by how it feels to inhabit.
                </p>
                <p>
                  Training in Qigong, an ancient meditation and energy practice, deepened this awareness. It revealed how energy moves through bodies and environments, and how our own state and intention play a crucial role.
                </p>
                <p>
                  This unique convergence of disciplines allows Amelia to sense the patterns held by the land—shaped by natural forces, human activity, and history—and to work towards restoring balance for people, animals, and ecosystems.
                </p>
                <p className="italic text-forest-green/80">
                  Amelia is deeply passionate about this work and looks forward to connecting with you.
                </p>
              </div>
            </div>

            <button 
              onClick={toggleExpand}
              className="group flex items-center justify-center md:justify-start gap-2 text-primary-clay font-medium hover:text-primary-rust transition-colors mt-2 focus:outline-none w-full md:w-auto"
            >
              <span>{isExpanded ? 'Read Less' : 'Read Full Journey'}</span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 transition-transform group-hover:-translate-y-1" />
              ) : (
                <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-1" />
              )}
            </button>
          </div>
        </div>

      </div>
    </SectionWrapper>
  );
};

export default Bio;
